/**
 * Local Drive preview stub for ?test=google-drive-extension.
 * Never iframes fake docs.google.com IDs (those show "file does not exist").
 */

import { Image as ImageIcon, Link2, FileText } from 'lucide-react'
import { GoogleDriveIcon } from '@/components/icons/google-drive-icon'
import { Button } from '@/components/ui/button'
import { OpenDestinationIcons } from '@/lib/open-destination-icons'
import {
  driveEffectiveMime,
  isDriveFileImportable,
} from '@/lib/drive-importability'
import type { MockDriveFileKind } from './mock-drive-tree'
import { mockMimeForKind } from './mock-drive-tree'
import { cn } from '@/lib/utils'

const OpenIcon = OpenDestinationIcons.open

export type HarnessPreviewFile = {
  id: string
  name: string
  type: MockDriveFileKind
  mimeType?: string
  shortcutTarget?: { id: string; mimeType: string; name?: string } | null
}

function previewCopy(file: HarnessPreviewFile): {
  title: string
  body: string
  kind: 'doc' | 'image' | 'shortcut-folder' | 'pdf' | 'other'
} {
  const mime = driveEffectiveMime(
    file.mimeType ?? mockMimeForKind(file.type),
    file.shortcutTarget?.mimeType,
  )
  if (file.type === 'shortcut' || file.mimeType === 'application/vnd.google-apps.shortcut') {
    if (mime === 'application/vnd.google-apps.folder') {
      return {
        kind: 'shortcut-folder',
        title: 'Shortcut to a folder',
        body: 'This Drive shortcut points to a folder. Open it in Google Drive to browse the original. Not importable as a Maater document.',
      }
    }
    return {
      kind: 'other',
      title: 'Shortcut - no direct preview',
      body: 'This is a Drive shortcut. Open it in Google Drive to view the original.',
    }
  }
  if (file.type === 'image' || mime.startsWith('image/')) {
    return {
      kind: 'image',
      title: 'Image preview',
      body: 'Harness stub. Production proxies the image with your Drive token. Images are not importable as text documents.',
    }
  }
  if (file.type === 'pdf' || mime === 'application/pdf') {
    return {
      kind: 'pdf',
      title: 'PDF - no embed preview',
      body: 'Harness stub. Production may proxy PDFs later. PDFs are not importable as text documents.',
    }
  }
  if (file.type === 'doc' || mime === 'application/vnd.google-apps.document') {
    return {
      kind: 'doc',
      title: 'Harness preview',
      body: 'Not a live Drive embed - fake Google Doc IDs break iframes. Importable as a Maater markdown note.',
    }
  }
  if (file.type === 'md' || mime === 'text/markdown') {
    return {
      kind: 'doc',
      title: 'Markdown file',
      body: 'Harness stub. Importable as a Maater note.',
    }
  }
  return {
    kind: 'other',
    title: 'Preview not available',
    body: 'This file type has no harness embed. Open in Google Drive instead.',
  }
}

export function HarnessDrivePreviewStub({
  file,
  onOpen,
  onImport,
  compact,
  className,
}: {
  file: HarnessPreviewFile
  onOpen?: () => void
  onImport?: () => void
  compact?: boolean
  className?: string
}) {
  const copy = previewCopy(file)
  const effectiveMime = driveEffectiveMime(
    file.mimeType ?? mockMimeForKind(file.type),
    file.shortcutTarget?.mimeType,
  )
  const canImport = isDriveFileImportable(effectiveMime)
  const Icon =
    copy.kind === 'image'
      ? ImageIcon
      : copy.kind === 'shortcut-folder'
        ? Link2
        : FileText

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-lg border border-border bg-card p-6 text-center',
        compact ? 'min-h-[180px]' : 'min-h-[280px]',
        className,
      )}
      data-testid="harness-drive-preview-stub"
    >
      {copy.kind === 'doc' ? (
        <GoogleDriveIcon className="h-10 w-10 text-muted-foreground" />
      ) : (
        <Icon className="h-10 w-10 text-muted-foreground" />
      )}
      <div className="space-y-1 max-w-xs">
        <p className="text-sm font-medium text-foreground">{copy.title}</p>
        <p className="text-xs text-muted-foreground leading-relaxed">{file.name}</p>
        <p className="text-xs text-muted-foreground leading-relaxed">{copy.body}</p>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {onOpen ? (
          <Button size="sm" className="h-8 gap-1.5 text-xs" onClick={onOpen}>
            <OpenIcon className="h-3.5 w-3.5" />
            Open
          </Button>
        ) : null}
        {canImport && onImport ? (
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={onImport}>
            Import to Maater
          </Button>
        ) : null}
      </div>
    </div>
  )
}
